import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShipmentNotificationParams } from '@/interfaces/shipmentNotification'
import APIServices from '@/api/APIServices'

interface CreateShipmentNotificationResponse {
  success: boolean
  data?: unknown
  message?: string
}

const createShipmentNotification = async (
  data: ShipmentNotificationParams
): Promise<CreateShipmentNotificationResponse> => {
  try {
    // Validate required fields
    if (!data.shouldArrivedAt) {
      throw new Error('shouldArrivedAt is required')
    }
    if (!data.providerCompanyId) {
      throw new Error('providerCompanyId is required')
    }
    if (!data.managerCompanyId) {
      throw new Error('managerCompanyId is required')
    }

    // Ensure data types are correct and create minimal request
    const requestData: {
      shouldArrivedAt: string
      providerCompanyId: number
      managerCompanyId: number
      name?: string
      observations?: string
      status?: string
    } = {
      shouldArrivedAt: data.shouldArrivedAt,
      providerCompanyId: Number(data.providerCompanyId),
      managerCompanyId: Number(data.managerCompanyId),
    }

    // Only add optional fields if they exist
    if (data.name && data.name.trim()) {
      requestData.name = data.name.trim()
    }

    if (data.observations && data.observations.trim()) {
      requestData.observations = data.observations.trim()
    }

    const response = await APIServices.post(
      '/cargo/shippingnotification',
      requestData
    )

    return {
      success: true,
      data: response.data,
      message: 'Notificación de embarque creada exitosamente',
    }
  } catch (error: unknown) {
    // Error creating shipment notification

    let errorMessage = 'Error al crear la notificación de embarque'

    if ((error as { response?: { data?: unknown } }).response?.data) {
      const errorData = (error as { response: { data: unknown } }).response.data
      if (typeof errorData === 'string') {
        errorMessage = errorData
      } else if (typeof errorData === 'object' && errorData !== null) {
        const errorObj = errorData as {
          message?: string
          error?: string
          errors?: unknown
        }
        if (errorObj.message) {
          errorMessage = errorObj.message
        } else if (errorObj.error) {
          errorMessage = errorObj.error
        } else if (errorObj.errors) {
          errorMessage = JSON.stringify(errorObj.errors)
        }
      }
    } else if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error
    ) {
      errorMessage = (error as { message: string }).message
    }

    return {
      success: false,
      message: errorMessage,
    }
  }
}

export const useCreateShipmentNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createShipmentNotification,
    onSuccess: (result) => {
      if (result.success) {
        // Invalidar la query de la lista para refrescar los datos
        queryClient.invalidateQueries({
          queryKey: ['shipment-notifications'],
        })
      }
    },
    onError: () => {
      // Mutation error handled by the caller
    },
  })
}
